import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import { it } from "./locales/it";
import { en } from "./locales/en";

const STORAGE_KEY = "lexai.lang";
const COOKIE_KEY = "lexai_lang";

function readCookie(name: string): string | null {
  if (typeof document === "undefined") return null;
  const m = document.cookie.match(new RegExp("(?:^|; )" + name + "=([^;]*)"));
  return m ? decodeURIComponent(m[1]) : null;
}

function writeCookie(name: string, value: string) {
  if (typeof document === "undefined") return;
  document.cookie = `${name}=${encodeURIComponent(value)}; path=/; max-age=31536000; SameSite=Lax`;
}

export function detectClientLang(): "it" | "en" {
  if (typeof window === "undefined") return "it";
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === "it" || stored === "en") return stored;
    const cookie = readCookie(COOKIE_KEY);
    if (cookie === "it" || cookie === "en") return cookie;
    const nav = (navigator.language || "it").toLowerCase();
    return nav.startsWith("en") ? "en" : "it";
  } catch {
    return "it";
  }
}

// Always initialize to "it" so SSR markup matches the very first client render.
// We switch to the user's preferred language right after hydration via applyClientLang().
if (!i18n.isInitialized) {
  i18n.use(initReactI18next).init({
    resources: { it: { translation: it }, en: { translation: en } },
    lng: "it",
    fallbackLng: "it",
    supportedLngs: ["it", "en"],
    interpolation: { escapeValue: false },
    react: { useSuspense: false },
  });
}

if (typeof window !== "undefined") {
  const sync = (lng: string) => {
    const code: "it" | "en" = lng?.toLowerCase().startsWith("en") ? "en" : "it";
    try {
      document.documentElement.lang = code;
      localStorage.setItem(STORAGE_KEY, code);
      writeCookie(COOKIE_KEY, code);
    } catch {}
  };
  i18n.on("languageChanged", sync);
}

export function applyClientLang() {
  if (typeof window === "undefined") return;
  const target = detectClientLang();
  if (i18n.resolvedLanguage !== target) {
    void i18n.changeLanguage(target);
  } else {
    try { document.documentElement.lang = target; } catch {}
  }
}

export default i18n;
